/**
 * Which module a session opens on when no `?open=` is present.
 *
 * The hub used to land everyone on Home. That is wrong twice over: a field
 * account on a phone has no use for a command overview and needs the field
 * companion immediately, and a staff role that only ever works one board (S2
 * the situation map, the ETB keeper the operational log) paid a navigation step
 * on every single sign-in to get where they always go.
 *
 * Two rules, in order:
 *  1. On a phone the field companion wins outright — that IS the mobile app.
 *  2. Otherwise the active role's own primary board, if the session can
 *     actually discover it.
 *
 * Home stays the fallback, and it stays reachable: this only changes where a
 * session STARTS, never what it can navigate to.
 */

/**
 * Role code -> the module id that role works from. Deliberately partial: a role
 * with no single obvious board (administrators, auditors, planners who move
 * between several) is better served by Home than by a guess.
 */
const ROLE_LANDING_MODULE: Readonly<Record<string, string>> = {
  // Field tier — the companion is their whole application.
  field_user: "field-operations",
  unit_leader: "field-operations",
  // Situation awareness lives on the map it maintains.
  s2_situation_reporting: "situation-cop",
  // The ETB keeper's board is the log itself.
  etb_keeper: "operational-log",
  // Dispatch and command work the operations surface.
  luk_dispatcher: "operations",
  incident_commander: "operations",
  ortsbeauftragter: "operations",
  s3_operations: "operations",
  // Single-desk specialists.
  technical_adviser: "adviser-field",
  registration_officer: "registration",
  schirrmeister: "assets",
  s4_logistics: "logistics",
  s1_personnel: "personnel",
};

const FIELD_ROLE_CODES = new Set([
  "field_helfer",
  "field_user",
  "order_recipient",
  "unit_leader",
  "zugfuehrer",
]);

export const HOME_LANDING_MODULE = "home";
export const FIELD_LANDING_MODULE = "field-operations";

export interface RoleLandingInput {
  /** `session.clientDevice`. */
  readonly device: "desktop" | "mobile" | "tablet";
  /** `session.activeContext?.roleCode`. */
  readonly roleCode: string | null | undefined;
  /**
   * Whether the session can actually reach a module id. Passing the real
   * discoverability check keeps this from ever landing someone on a screen the
   * shell would refuse to open — which would look identical to a broken app.
   */
  readonly canOpen: (moduleId: string) => boolean;
}

export interface LandingModuleRequestInput {
  readonly device: "desktop" | "mobile" | "tablet";
  readonly explicitModule: string | null;
  readonly landingModule: string;
}

/**
 * Resolves the URL module against the session landing module. Home is also the
 * login/brand URL, so it must not strand a field phone outside its dedicated
 * companion. Every other explicit module remains a genuine deep link.
 */
export function resolveLandingModuleRequest({
  device,
  explicitModule,
  landingModule,
}: LandingModuleRequestInput): string {
  if (explicitModule === null) return landingModule;
  if (
    device === "mobile" &&
    explicitModule === HOME_LANDING_MODULE &&
    landingModule === FIELD_LANDING_MODULE
  ) {
    return landingModule;
  }
  return explicitModule;
}

export function defaultLandingModule({ canOpen, device, roleCode }: RoleLandingInput): string {
  const normalized = roleCode?.trim().toLowerCase();
  if (
    device === "mobile" &&
    normalized !== undefined &&
    FIELD_ROLE_CODES.has(normalized) &&
    canOpen(FIELD_LANDING_MODULE)
  ) {
    return FIELD_LANDING_MODULE;
  }
  const preferred = normalized === undefined ? undefined : ROLE_LANDING_MODULE[normalized];
  if (preferred !== undefined && canOpen(preferred)) return preferred;
  return HOME_LANDING_MODULE;
}
